import {
  type IFormGenUiInputSchema,
  type TFormGenRenderAs,
  type TJSONSchema,
  getJsonSchemaElement,
  resolveRenderAs,
} from "@artifact-management/json-dynamic-form";
import type { ReactNode } from "react";
import type { FieldValues } from "react-hook-form";
import type { IFormGenComponentProps } from "../index";
import { FormGenInputAddress } from "./Address";
import { FormGenInputAppellation } from "./Appellation";
import { FormGenInputAutocomplete } from "./Autocomplete";
import { FormGenInputCheckbox } from "./Checkbox";
import { FormGenInputChips } from "./Chips";
import { FormGenInputContact } from "./Contact";
import { FormGenInputDate } from "./DatePicker";
import { FormGenInputDateRange } from "./DateRange";
import { FormGenInputEnterprise } from "./Enterprise";
import { FormGenInputFileUpload } from "./FileUpload";
import { FormGenInputJobApply } from "./JobApply";
import { FormGenInputNumber } from "./Number";
import { FormGenInputPerimeter } from "./Perimeter";
import { FormGenInputPlaces } from "./Places";
import { FormGenInputRadio } from "./Radio";
import { FormGenInputRichText } from "./RichText";
import { FormGenInputSelect } from "./Select";
import { FormGenInputStructurePartner } from "./StructurePartner";
import { FormGenInputSwitch } from "./Switch";
import { FormGenInputText } from "./Text";
import { FormGenInputTextarea } from "./Textarea";
import { FormGenInputTrainingApply } from "./TrainingApply";
import { FormGenInputUrl } from "./Url";
import { getFormGenRawEnumOptions } from "./logics";

export interface IFormGenInputProps<Schema extends TJSONSchema, T extends FieldValues>
  extends IFormGenComponentProps<Schema, T> {
  uiSchemaElement: IFormGenUiInputSchema<Schema>;
  jsonSchemaElement: TJSONSchema;
  multiple?: boolean;
  enumOptions: unknown[];
}

// Single mapping from a render variant to a component. Every component receives the
// same props; variant-specific options (enum values, keyword options...) are read
// from jsonSchemaElement by the component itself.
const FORM_GEN_INPUT_COMPONENTS: {
  [Variant in TFormGenRenderAs]: <Schema extends TJSONSchema, T extends FieldValues>(
    props: IFormGenInputProps<Schema, T>
  ) => ReactNode;
} = {
  text: FormGenInputText,
  textarea: FormGenInputTextarea,
  richtext: FormGenInputRichText,
  date: FormGenInputDate,
  url: FormGenInputUrl,
  number: FormGenInputNumber,
  select: FormGenInputSelect,
  radio: FormGenInputRadio,
  chips: FormGenInputChips,
  checkbox: FormGenInputCheckbox,
  switch: FormGenInputSwitch,
  setting: FormGenInputAutocomplete,
  jobApply: FormGenInputJobApply,
  trainingApply: FormGenInputTrainingApply,
  address: FormGenInputAddress,
  appellation: FormGenInputAppellation,
  places: FormGenInputPlaces,
  enterprise: FormGenInputEnterprise,
  contact: FormGenInputContact,
  fileUpload: FormGenInputFileUpload,
  dateRange: FormGenInputDateRange,
  provider: FormGenInputStructurePartner,
  perimeter: FormGenInputPerimeter,
};

export const FormGenInput = <Schema extends TJSONSchema, T extends FieldValues>(
  props: Omit<IFormGenInputProps<Schema, T>, "jsonSchemaElement" | "multiple" | "enumOptions">
) => {
  const { schema, uiSchemaElement, parsedJsonSchema } = props;
  const jsonSchemaElement = getJsonSchemaElement(schema.jsonSchema, uiSchemaElement.scope);

  if (!jsonSchemaElement) {
    throw new Error(
      `JSON schema element not found for scope: ${uiSchemaElement.scope}\nUI schema element: ${JSON.stringify(uiSchemaElement)}`
    );
  }

  const { variant, multiple } = resolveRenderAs(jsonSchemaElement, uiSchemaElement.renderAs);
  const Component = FORM_GEN_INPUT_COMPONENTS[variant];
  const enumOptions = getFormGenRawEnumOptions(jsonSchemaElement, multiple, uiSchemaElement.scope, parsedJsonSchema);
  return <Component {...props} jsonSchemaElement={jsonSchemaElement} multiple={multiple} enumOptions={enumOptions} />;
};
